import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  generateId,
  useOwnerId,
  usePlantPhotosData,
  usePlantsData,
  type Plant,
  type PlantPhoto,
} from "~/state/sync";
import { mk, type MutationVars } from "~/state/mutations";
import type {
  PlantAcquisition,
  PlantPlacement,
  PlantStatus,
} from "~/api/types";

export type PlantInput = {
  collectionId: string;
  taxonId?: string | null;
  nickname?: string | null;
  fallbackName?: string;
  status?: PlantStatus;
  placement?: PlantPlacement;
  acquisition?: PlantAcquisition;
  quantity?: number;
  acquiredOn?: string | null;
  acquiredFrom?: string | null;
  plantedOn?: string | null;
  potSizeMm?: number | null;
  notes?: string | null;
  tags?: string[];
};

export type PlantPlacementInput =
  | { kind: "geo"; lat: number; lng: number }
  | { kind: "custom"; x: number; y: number }
  | { kind: "clear" };

function byName(a: Plant, b: Plant) {
  return a.display_name.localeCompare(b.display_name);
}

export function usePlants(collectionId?: string) {
  const { data } = usePlantsData();
  return useMemo(() => {
    return (data ?? [])
      .filter((plant) => !collectionId || plant.collection_id === collectionId)
      .slice()
      .sort(byName);
  }, [data, collectionId]);
}

export function usePlant(plantId: string | undefined) {
  const { data } = usePlantsData();
  return useMemo(
    () =>
      plantId ? ((data ?? []).find((p) => p.id === plantId) ?? null) : null,
    [data, plantId],
  );
}

export function usePlantCount() {
  const { data } = usePlantsData();
  return data?.length ?? 0;
}

export function usePlantPhotos(plantId: string | undefined) {
  const { data } = usePlantPhotosData();
  return useMemo(() => {
    if (!plantId) return [] as PlantPhoto[];
    return (data ?? [])
      .filter((photo) => photo.plant_id === plantId)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order);
  }, [data, plantId]);
}

export function usePlantActions() {
  const userId = useOwnerId();
  const { data: photoData } = usePlantPhotosData();
  const createM = useMutation<unknown, Error, MutationVars["plantCreate"]>({
    mutationKey: [...mk.plantCreate],
  });
  const updateM = useMutation<unknown, Error, MutationVars["plantUpdate"]>({
    mutationKey: [...mk.plantUpdate],
  });
  const removeM = useMutation<unknown, Error, MutationVars["plantRemove"]>({
    mutationKey: [...mk.plantRemove],
  });
  const photoCreateM = useMutation<unknown, Error, MutationVars["photoCreate"]>(
    {
      mutationKey: [...mk.photoCreate],
    },
  );
  const photoRemoveM = useMutation<unknown, Error, MutationVars["photoRemove"]>(
    {
      mutationKey: [...mk.photoRemove],
    },
  );

  function requireUser() {
    if (!userId) throw new Error("You must be signed in.");
    return userId;
  }

  function create(input: PlantInput) {
    const uid = requireUser();
    const id = generateId();
    const now = new Date().toISOString();
    const row: Plant = {
      id,
      collection_id: input.collectionId,
      owner_id: uid,
      taxon_id: input.taxonId ?? null,
      nickname: input.nickname?.trim() || null,
      // Overwritten by the server trigger; set locally so offline lists have a
      // label immediately instead of a blank row.
      display_name:
        input.nickname?.trim() ||
        input.fallbackName?.trim() ||
        "Unidentified plant",
      status: input.status ?? "stable",
      placement: input.placement ?? "pot",
      acquisition: input.acquisition ?? "unknown",
      quantity: input.quantity ?? 1,
      acquired_on: input.acquiredOn ?? null,
      acquired_from: input.acquiredFrom?.trim() || null,
      planted_on: input.plantedOn ?? null,
      pot_size_mm: input.potSizeMm ?? null,
      notes: input.notes?.trim() || null,
      tags: input.tags ?? [],
      cover_photo_path: null,
      map_lat: null,
      map_lng: null,
      map_x: null,
      map_y: null,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    createM.mutate({ userId: uid, row });
    return id;
  }

  function update(plantId: string, input: Partial<PlantInput>) {
    const uid = requireUser();
    const fields: Partial<Plant> = {};
    if (input.collectionId !== undefined) {
      fields.collection_id = input.collectionId;
    }
    if (input.taxonId !== undefined) fields.taxon_id = input.taxonId;
    if (input.nickname !== undefined) {
      const nickname = input.nickname?.trim() || null;
      fields.nickname = nickname;
      fields.display_name =
        nickname || input.fallbackName?.trim() || "Unidentified plant";
    }
    if (input.status !== undefined) fields.status = input.status;
    if (input.placement !== undefined) fields.placement = input.placement;
    if (input.acquisition !== undefined) fields.acquisition = input.acquisition;
    if (input.quantity !== undefined) fields.quantity = input.quantity;
    if (input.acquiredOn !== undefined) fields.acquired_on = input.acquiredOn;
    if (input.acquiredFrom !== undefined) {
      fields.acquired_from = input.acquiredFrom?.trim() || null;
    }
    if (input.plantedOn !== undefined) fields.planted_on = input.plantedOn;
    if (input.potSizeMm !== undefined) fields.pot_size_mm = input.potSizeMm;
    if (input.notes !== undefined) fields.notes = input.notes?.trim() || null;
    if (input.tags !== undefined) fields.tags = input.tags;
    if (Object.keys(fields).length === 0) return;
    updateM.mutate({ userId: uid, id: plantId, fields });
  }

  function place(plantId: string, placement: PlantPlacementInput) {
    const uid = requireUser();
    let fields: Partial<Plant>;
    if (placement.kind === "geo") {
      fields = {
        map_lat: placement.lat,
        map_lng: placement.lng,
        map_x: null,
        map_y: null,
      };
    } else if (placement.kind === "custom") {
      fields = {
        map_x: placement.x,
        map_y: placement.y,
        map_lat: null,
        map_lng: null,
      };
    } else {
      fields = { map_lat: null, map_lng: null, map_x: null, map_y: null };
    }
    updateM.mutate({ userId: uid, id: plantId, fields });
  }

  function setCoverPhoto(plantId: string, storagePath: string | null) {
    const uid = requireUser();
    updateM.mutate({
      userId: uid,
      id: plantId,
      fields: { cover_photo_path: storagePath },
    });
  }

  function addPhoto(plantId: string, storagePath: string, caption?: string) {
    const uid = requireUser();
    const id = generateId();
    const now = new Date().toISOString();
    const existing = (photoData ?? []).filter(
      (photo) => photo.plant_id === plantId,
    );
    const row: PlantPhoto = {
      id,
      plant_id: plantId,
      owner_id: uid,
      storage_path: storagePath,
      caption: caption?.trim() || null,
      taken_at: now,
      sort_order: existing.length,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    photoCreateM.mutate({
      userId: uid,
      row,
      coverForPlantId: existing.length === 0 ? plantId : undefined,
    });
    return id;
  }

  function removePhoto(photoId: string) {
    const uid = requireUser();
    photoRemoveM.mutate({ userId: uid, id: photoId });
  }

  function remove(plantId: string) {
    const uid = requireUser();
    removeM.mutate({ userId: uid, id: plantId });
  }

  return {
    create,
    update,
    place,
    setCoverPhoto,
    addPhoto,
    removePhoto,
    remove,
  };
}
